import React, { Component } from 'react';
import { Input, Popover, Spin, Empty, Button, Popconfirm, Tooltip } from 'antd';
import { api } from '@unicus/react-ui-kit';
import CustomScrollbars from 'components/CustomScrollbars';
import debounce from 'lodash/debounce';
import {
  CheckOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined
} from '@ant-design/icons';
import CrudEdit from './CrudEdit';

class RemoteSelectPopover extends Component {
  constructor(props) {
    super(props);

    this.handleSearch = debounce(this.fetchData, 800);
    this.lastFetchId = 0;
    this.fethOnMount = props.fethOnMount;
    this.refInput = React.createRef();
    this.refCrud = React.createRef();

    this.state = {
      data: [],
      fetching: false,
      visible: false,
      textSearched: '',
      pagination: {
        hasNextPage: true,
        currentPage: 0
      }
    };
  }

  componentDidMount() {
    if (this.fethOnMount) {
      this.fetchData();
    }
  }

  fetchData = (value) => {
    const { pagination } = this.state;
    const { resource, params = {} } = this.props;

    if (!pagination.hasNextPage) {
      return;
    }

    this.setState({ fetching: true });
    this.lastFetchId += 1;
    const fetchId = this.lastFetchId;

    const filter = {
      filterText: value,
      page: pagination.currentPage + 1,
      ...params
    };

    api.fetch(resource, filter).then((response = {}) => {
      const { data = { result: {} } } = response;
      if (fetchId !== this.lastFetchId) {
        return;
      }
      const { totalCount = 0, items = [] } = data.result;

      this.setState((prevState) => ({
        ...prevState,
        data: filter.page === 1 ? items : [...prevState.data, ...items],
        fetching: false,
        textSearched: value,
        pagination: {
          hasNextPage:
            filter.page === 1
              ? items.length < totalCount
              : prevState.data.length + items.length < totalCount,
          currentPage: filter.page
        }
      }));
    });
  };

  isSelect = (record) => {
    const { values } = this.props;
    return !values.some((c) => c.id === record.id);
  };

  handleAdd = (record) => {
    const { onSelect, multiple } = this.props;

    if (!multiple) {
      this.setState({ visible: false });
    }

    onSelect(record);
  };

  handleRemove = (record) => {
    const { onRemove, multiple } = this.props;

    if (!multiple) {
      this.setState({ visible: false });
    }

    onRemove(record);
  };

  handleOnChangeVisible = (visible) => {
    if (this.props.disabled) {
      return;
    }

    if (!visible) {
      this.lastFetchId = 0;

      this.setState({
        data: [],
        fetching: false,
        visible: false,
        textSearched: undefined,
        pagination: {
          hasNextPage: true,
          currentPage: 0
        }
      });

      this.clearSearchField();
    }

    if (visible) {
      this.fetchData();
    }

    this.setState({ visible });
  };

  handleOnSearch = (value) => {
    this.setState({ pagination: { hasNextPage: true, currentPage: 0 } });
    this.handleSearch(value);
  };

  clearSearchField = () => {
    if (this.refInput.current) {
      this.refInput.current.state.value = '';
    }
  };

  handleOnScroll = () => {
    const { textSearched } = this.state;
    this.fetchData(textSearched);
  };

  renderItem = (record, add, remove) => {
    const {
      values = [],
      loadingId,
      customButtonAdd,
      customButtonRemove
    } = this.props;

    const loading = loadingId === record.id;
    const isSelected = values.some((c) => c.id === record.id);

    return (
      <div
        className={`gx-remoteselectpopover-content-item ${
          isSelected ? 'selected' : ''
        }`}
      >
        <span>{record?.name}</span>

        <span>
          {loading ? <LoadingOutlined /> : null}
          {isSelected && !loading ? (
            customButtonRemove ? (
              customButtonRemove(() => remove(record))
            ) : (
              <Tooltip title="Remover">
                <Popconfirm
                  title="Deseja remover?"
                  onConfirm={() => remove(record)}
                  cancelText="Não"
                  okText="Sim"
                >
                  <DeleteOutlined />
                </Popconfirm>
              </Tooltip>
            )
          ) : null}

          {!isSelected && !loading ? (
            customButtonAdd ? (
              customButtonAdd(() => add(record))
            ) : (
              <Tooltip title="Adicionar">
                <PlusOutlined onClick={() => add(record)} />
              </Tooltip>
            )
          ) : null}
        </span>
      </div>
    );
  };

  handleCreateOrUpdateSuccess = (response) => {
    const { onSelect } = this.props;

    if (onSelect) {
      onSelect(response.data.result);
    }

    this.clearSearchField();
  };

  handleClickCreate = () => {
    if (this.refCrud.current) {
      this.refCrud.current.create({ name: this.state.textSearched });
      this.handleOnChangeVisible(false);
    }
  };

  render() {
    const { fetching, visible, data } = this.state;
    const {
      children,
      renderItem,
      placeholder,
      addButtonText,
      editComponent,
      resource,
      placement = 'rightTop'
    } = this.props;

    return (
      <CrudEdit
        ref={this.refCrud}
        editComponent={editComponent}
        resource={resource}
        initialValues={{ name: this.state.textSearched }}
        onCreateOrUpdateSuccess={this.handleCreateOrUpdateSuccess}
      >
        <Popover
          placement={placement}
          trigger="click"
          visible={visible}
          onVisibleChange={this.handleOnChangeVisible}
          content={
            <div className="gx-remoteselectpopover-content">
              <Input
                className="gx-remoteselectpopover-content-input"
                maxLength={40}
                ref={this.refInput}
                type="search"
                onPressEnter={this.handleOnPressEnter}
                onChange={(e) => this.handleOnSearch(e.target.value)}
                placeholder={placeholder || 'Pesquisar...'}
              />

              <CustomScrollbars
                style={{ display: 'flex', height: '150px' }}
                onScrollEnd={this.handleOnScroll}
              >
                {data.length === 0 && !fetching ? (
                  <div>
                    <Empty
                      description=""
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </div>
                ) : null}
                {data.map((record) =>
                  renderItem
                    ? renderItem(record, this.handleAdd, this.handleRemove)
                    : this.renderItem(record, this.handleAdd, this.handleRemove)
                )}
                {fetching ? (
                  <Spin className="gx-remoteselectpopover-content-spin" />
                ) : null}
              </CustomScrollbars>
              <Button type="link" onClick={this.handleClickCreate}>
                <PlusOutlined />
                {addButtonText ?? 'Adicionar'}
              </Button>
            </div>
          }
        >
          {children}
        </Popover>
      </CrudEdit>
    );
  }
}

RemoteSelectPopover.defaultProps = { fethOnMount: false };

export default RemoteSelectPopover;
